/**
 * Client wrappers for `/api/order-item-manual-units/*` — the per-order pack
 * names used by Fascia Gutter unitwise manual line items.
 */

import axios from 'axios';
import { buildApiUrl, tokenManager } from '../config/api.config';
import { logger } from '../utils/logger';

const authHeaders = () => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Authentication token not found');
  return {
    'x-access-token': token,
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };
};

const wrapError = (error, context) => {
  logger.error(`[orderItemManualUnitApi] ${context} failed`, error);
  if (error.response) {
    const data = error.response.data || {};
    return {
      success: false,
      status: error.response.status,
      error: data.error || context + ' failed',
      message: data.message,
      details: data.details
    };
  }
  if (error.request) {
    return { success: false, error: 'Network error', message: 'No response from server.' };
  }
  return { success: false, error: error.message };
};

export const createPackUnits = async (orderId, units, department) => {
  try {
    const url = buildApiUrl(`api/order-item-manual-units/${orderId}/bulk`);
    const response = await axios.post(url, { units, department }, { headers: authHeaders() });
    return { success: true, units: response.data.units || [] };
  } catch (error) {
    return wrapError(error, 'createPackUnits');
  }
};

export const listPackUnits = async (orderId, department) => {
  try {
    const url = buildApiUrl(`api/order-item-manual-units/${orderId}${department ? `?department=${department}` : ''}`);
    const response = await axios.get(url, { headers: authHeaders() });
    return { success: true, units: response.data.units || [], assignedCount: response.data.assignedCount || 0 };
  } catch (error) {
    return wrapError(error, 'listPackUnits');
  }
};

// Turns pack wise off for an order + department: unassigns the line items but
// keeps the pack names so they can be re-selected later.
export const unassignPackUnits = async (orderId, department) => {
  try {
    const url = buildApiUrl(`api/order-item-manual-units/${orderId}/unassign`);
    const response = await axios.post(url, { department }, { headers: authHeaders() });
    return { success: true, clearedItems: response.data.clearedItems || 0 };
  } catch (error) {
    return wrapError(error, 'unassignPackUnits');
  }
};

export const renamePackUnit = async (unitId, unitName) => {
  try {
    const url = buildApiUrl(`api/order-item-manual-units/${unitId}`);
    const response = await axios.patch(url, { unit_name: unitName }, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'renamePackUnit');
  }
};

export const deletePackUnit = async unitId => {
  try {
    const url = buildApiUrl(`api/order-item-manual-units/${unitId}`);
    const response = await axios.delete(url, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'deletePackUnit');
  }
};
